import { ModelResource, type PaginatedResult } from './resource';
import { BankTransactionBase, BankTransactionType, Payment, Remittance } from '../models';
import type { AxiosRequestConfig } from 'axios';
import { v2ParamsSerializer } from '@stuut/common/src/api_client';
import dayjs from 'dayjs';

export interface CashAppWorkspaceData {
  payments: Payment[];
  remittances: Remittance[];
}

export interface CreateBankTransactionData {
  account_id: string;

  statement_id?: string;

  reference_number: string;

  amount: number;

  type: string;

  description?: string;

  timestamp?: string;
}

export interface BulkMarkHandledOutsideResult {
  transaction_id: string;
  success: boolean;
  error?: string;
}

export interface BulkMarkHandledOutsideResponse {
  results: BulkMarkHandledOutsideResult[];
}

export interface BulkMarkNonPaymentResult {
  transaction_id: string;
  success: boolean;
  error?: string;
}

export interface BulkMarkNonPaymentResponse {
  results: BulkMarkNonPaymentResult[];
}

export interface UpdateBankTransactionData {
  is_payment?: boolean | null;

  non_payment_reason?: string | null;

  amount_handled_outside?: number;

  handled_outside_reason?: string | null;

  // draft only update fields
  timestamp?: string;

  reference_number?: string;

  description?: string;

  type?: string;

  amount?: number;
}

export class BankTransactions extends ModelResource<
  BankTransactionBase,
  CreateBankTransactionData
> {
  model = BankTransactionBase;

  resourcePath = 'v1/bank-transactions';

  // batch approve all payments related to txn
  async getWorkspace(
    txn: BankTransactionBase,
    params?: { entry_payment_id?: string; entry_remittance_id?: string },
  ): Promise<CashAppWorkspaceData> {
    return this.api.get(this.path(txn.id, 'workspace'), { params }).then((res) => {
      return {
        payments: res.data.payments.map((p: any) => Payment.fromJson(p)),
        remittances: res.data.remittances.map((r: any) => Remittance.fromJson(r)),
      };
    });
  }

  async get(transactionId: string): Promise<BankTransactionBase> {
    const response = await this.api.get(`v2/bank-transactions/${transactionId}`);
    return BankTransactionBase.fromJson(response.data);
  }

  async create(data: CreateBankTransactionData): Promise<BankTransactionBase> {
    const response = await this.api.post('v2/bank-transactions', { data });
    return BankTransactionBase.fromJson(response.data);
  }

  async update(
    transactionId: string,
    args: { data: UpdateBankTransactionData },
  ): Promise<BankTransactionBase> {
    const response = await this.api.patch(`v2/bank-transactions/${transactionId}`, args);
    return BankTransactionBase.fromJson(response.data);
  }

  async list(args?: AxiosRequestConfig, paginatedResult?: false): Promise<BankTransactionBase[]>;

  async list(
    args?: AxiosRequestConfig,
    paginatedResult?: true,
  ): Promise<PaginatedResult<BankTransactionBase>>;

  async list(
    args?: AxiosRequestConfig,
    paginatedResult?: boolean,
  ): Promise<BankTransactionBase[] | PaginatedResult<BankTransactionBase>> {
    const params = args?.params as Record<string, unknown> | undefined;

    const response = await this.api.get('v2/bank-transactions', {
      ...args,
      params,
      paramsSerializer: v2ParamsSerializer,
    });
    const responseData = response.data as PaginatedResult<BankTransactionBase>;

    // Deserialize to model instances
    const serializedData = responseData.data.map((json) => BankTransactionBase.fromJson(json));

    // Return based on paginatedResult parameter
    if (paginatedResult) {
      return {
        data: serializedData,
        total_records: responseData.total_records,
        current_page: responseData.current_page,
        next_page: responseData.next_page,
      };
    }

    return serializedData;
  }

  async bulkMarkHandledOutside(
    transaction_ids: string[],
    handled_outside_reason?: string,
  ): Promise<BulkMarkHandledOutsideResponse> {
    const response = await this.api.post('v2/bank-transactions/bulk/mark-handled-outside', {
      data: { transaction_ids, handled_outside_reason },
    });
    return response.data as BulkMarkHandledOutsideResponse;
  }

  async bulkMarkNonPayment(
    transaction_ids: string[],
    non_payment_reason?: string,
  ): Promise<BulkMarkNonPaymentResponse> {
    const response = await this.api.post('v2/bank-transactions/bulk/mark-non-payment', {
      data: { transaction_ids, non_payment_reason },
    });
    return response.data as BulkMarkNonPaymentResponse;
  }

  async discardRemittance(
    transactionId: string,
    remittanceId: string,
    deleteOrphanedPayments: boolean = false,
  ): Promise<void> {
    await this.api.post(`v2/bank-transactions/${transactionId}/discard-remittance`, {
      data: {
        remittance_id: remittanceId,
        delete_orphaned_payments: deleteOrphanedPayments,
      },
    });
  }
}
